// =============================================================================
// selectors/backtests.ts — the seam for the Backtesting tab.
// -----------------------------------------------------------------------------
// `ui/` may not import `domain/` at all, so every number the tab shows arrives
// through here: the per-sample derivation, the two stats columns, the grouping,
// and the formatting that matches beta's rounding to the digit.
//
// ── 🚨 THE LIVE COLUMN IS THE WHOLE BOOK, NOT THE SELECTED ACCOUNT ──
//
// Beta computes the live comparison from `state.trades.filter(countsForMetrics)`
// — every metric-eligible trade of the pattern across all of history, regardless
// of the account chip. So `backtestLiveUniverse` filters by `countsForMetrics`
// only; it deliberately does NOT run `tradesForSelection`. Scoping the live
// column to the currently-selected account would make "how this setup actually
// went" mean something different every time the chip moved, and it would diverge
// from beta the moment a single account was picked.
// =============================================================================

import { countsForMetrics } from '@domain/trade';
import {
  backtestDerived, backtestGroupStats, backtestLiveStats,
  type BacktestStats, type LiveTradeStat,
} from '@domain/backtests';
import type { BacktestRecord } from '@io/sync/backtests';
import type { PatternSummary } from '@io/sync/patterns';
import type { TradeTableRow } from '@io/sync/trades';
import {
  metricsContextFactory, type AccountFilterAccount, type AccountSelection,
} from './accounts';

// ── re-exports (ui/ reaches the maths through here) ──────────────────────────
export { backtestDerived, backtestGroupStats, backtestLiveStats };
export type { BacktestStats, BacktestDerived, BacktestOutcome, LiveTradeStat } from '@domain/backtests';
export type { BacktestRecord } from '@io/sync/backtests';

// ── beta's btFmt* — the exact rounding the stats table and rows render ───────
export const fmtR = (v: number | null): string =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`;
export const fmtPts = (v: number | null): string =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${Math.round(v * 100) / 100}`;
export const fmtPct = (v: number | null): string =>
  v == null ? '—' : `${Math.round(v)}%`;
export const fmtRatio = (v: number | null): string =>
  v == null ? '—' : v.toFixed(2);

/**
 * The sample-size chip beside a pattern name: <10 collecting, 10–29 building,
 * 30+ meaningful. Same thresholds and wording as beta's `btSampleBadge`.
 */
export function sampleBadge(n: number): { cls: 'low' | 'mid' | 'ok'; hint: string } {
  if (n >= 30) return { cls: 'ok', hint: 'meaningful sample' };
  if (n >= 10) return { cls: 'mid', hint: 'building — aim for 30+' };
  return { cls: 'low', hint: 'collecting — aim for 30+' };
}

/** `p.candidateVersion || p.liveVersion || 1` — beta's `versionTestedFor`. */
export function versionTestedFor(
  patterns: readonly PatternSummary[] | null, patternId: string,
): number | null {
  const p = (patterns ?? []).find((x) => x.id === patternId);
  if (!p) return null;
  return p.candidateVersion || p.liveVersion || 1;
}

// ── the live universe ────────────────────────────────────────────────────────

/** A metric-eligible live trade, carrying its pattern so a group can filter it. */
export type LivePatternTrade = LiveTradeStat & { patternId: string | null };

/**
 * Whole-book, metric-eligible live trades — the population the live column reads.
 * ⚠ No `tradesForSelection`: the account chip only lifts the excluded-account
 * exclusion (via the context), it does not scope which accounts are counted.
 */
export function backtestLiveUniverse(
  rows: readonly TradeTableRow[],
  selection: AccountSelection,
  accounts: readonly AccountFilterAccount[],
): LivePatternTrade[] {
  const ctxFor = metricsContextFactory(accounts, selection);
  return rows
    .filter((t) => countsForMetrics(t, ctxFor(t)))
    .map((t) => ({
      patternId: t.patternId,
      netPnl: t.netPnl,
      rrRealized: t.rrRealized,
      maePoints: t.maePoints,
      mfePoints: t.mfePoints,
      points: t.points,
    }));
}

// ── grouping ─────────────────────────────────────────────────────────────────

export interface BacktestGroup {
  /** The pattern id, or '' for samples with no pattern. */
  patternId: string;
  /** Pattern name, or '(deleted pattern)' / '(no pattern)'. */
  name: string;
  isCandidate: boolean;
  /** Newest-first, matching beta's `date|createdAt` descending sort. */
  records: readonly BacktestRecord[];
  /** The hand-recorded sample side. */
  stats: BacktestStats;
  /** The real-trade side. `n === 0` when the pattern has no live trades. */
  live: BacktestStats;
}

/** `date|createdAt`, the key beta sorts records and groups by. */
const recKey = (b: BacktestRecord): string => `${b.date || ''}|${b.createdAt || ''}`;

/**
 * Group samples by pattern, newest activity first, each with both stats columns.
 * `patterns` names the groups and flags candidates; `live` is the universe from
 * `backtestLiveUniverse`.
 */
export function buildBacktestGroups(
  records: readonly BacktestRecord[],
  patterns: readonly PatternSummary[] | null,
  live: readonly LivePatternTrade[],
): BacktestGroup[] {
  const byPattern = new Map<string, BacktestRecord[]>();
  for (const b of records) {
    const arr = byPattern.get(b.patternId);
    if (arr) arr.push(b); else byPattern.set(b.patternId, [b]);
  }

  const groups: BacktestGroup[] = [];
  for (const [pid, recs] of byPattern) {
    const sorted = recs.slice().sort((a, b) => recKey(b).localeCompare(recKey(a)));
    const p = (patterns ?? []).find((x) => x.id === pid);
    const name = p ? p.name : (pid ? '(deleted pattern)' : '(no pattern)');
    // 🔴 NO PATTERN MEANS NO COMPARISON — AND `?? ''` IS WHAT BROKE THAT.
    //
    // Samples with no pattern are grouped under the empty key. The filter used
    // to read `(t.patternId ?? '') === pid`, which turns a null pattern into
    // that same empty string — so the "(no pattern)" group was compared against
    // EVERY UNTAGGED TRADE IN THE BOOK. A backtest of one idea, benchmarked
    // against a population it has nothing to do with, and the row looked exactly
    // like a real one.
    //
    // ⚠ Beta suppresses it structurally rather than by a rule: it matches
    // `t.tagging.patternId === patternId`, and an untagged trade arrives as
    // NULL from the database (never ''), so the comparison simply never
    // succeeds. This is that behaviour made explicit.
    //
    // ⚠ A group that HAS a pattern is untouched — `t.patternId === pid` already
    // excludes nulls, so the fix is refusing the empty key, not dropping the
    // comparison. `live.n === 0` is what hides the row (Backtests.tsx).
    const liveForPattern = pid ? live.filter((t) => t.patternId === pid) : [];
    groups.push({
      patternId: pid,
      name,
      isCandidate: !!p && p.status === 'candidate',
      records: sorted,
      stats: backtestGroupStats(sorted),
      live: backtestLiveStats(liveForPattern),
    });
  }

  // Groups by their newest record, newest first.
  return groups.sort((a, b) =>
    recKey(b.records[0] ?? emptyKeyRec).localeCompare(recKey(a.records[0] ?? emptyKeyRec)));
}

const emptyKeyRec = { date: '', createdAt: '' } as BacktestRecord;

/** Patterns with no samples yet — the "Pick a pattern to test…" options. */
export function untestedPatterns(
  records: readonly BacktestRecord[],
  patterns: readonly PatternSummary[] | null,
): readonly PatternSummary[] {
  const tested = new Set(records.map((b) => b.patternId));
  return (patterns ?? []).filter((p) => !tested.has(p.id));
}

// ── the new-sample builder ───────────────────────────────────────────────────

export interface NewBacktestOpts {
  /** A fresh unique id — the caller owns the generator (selectors have no clock). */
  id: string;
  /** ISO timestamp for `createdAt`. */
  nowIso: string;
  /** 'YYYY-MM-DD' for the default date. */
  today: string;
  /** Existing samples, to reuse the pattern's last contract. */
  existing: readonly BacktestRecord[];
  contracts: readonly { symbol: string }[];
  /** ⚠ Loosely typed — `prefs.defaultRiskByInstrument` is `Record<string, unknown>`;
   *  `posNum` coerces whatever the per-instrument entry actually holds. */
  defaultRisk: Record<string, unknown> | null;
  versionTested: number | null;
}

const posNum = (v: unknown): number | null => {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : null;
};

/**
 * A blank sample for a pattern, pre-filling contract and default risk exactly as
 * beta's `newRecord` does: the pattern's most-recent contract, else ES if the
 * catalogue has it, else the first contract; risk/RR from the per-instrument
 * defaults when positive.
 */
export function newBacktestRecord(patternId: string, opts: NewBacktestOpts): BacktestRecord {
  const group = opts.existing
    .filter((b) => b.patternId === patternId)
    .sort((a, b) => recKey(b).localeCompare(recKey(a)));
  const lastContract = group.length ? group[0]!.contract : '';
  const hasEs = opts.contracts.some((c) => c.symbol === 'ES');
  const contract = lastContract || (hasEs ? 'ES' : (opts.contracts[0]?.symbol ?? ''));
  const def = (opts.defaultRisk?.[contract] ?? {}) as { riskPoints?: unknown; rrPlanned?: unknown };
  return {
    id: opts.id,
    patternId,
    date: opts.today,
    side: '',
    contract,
    entry: null,
    exit: null,
    riskPoints: posNum(def.riskPoints),
    rrPlanned: posNum(def.rrPlanned),
    maePoints: null,
    mfePoints: null,
    timeIn: '',
    timeOut: '',
    session: '',
    regime: '',
    newsEvent: '',
    exitModelId: '',
    criteriaMet: {},
    confluences: {},
    notes: '',
    screenshots: [],
    versionTested: opts.versionTested,
    createdAt: opts.nowIso,
    extras: {},
  };
}
